import React from 'react';
import algoliasearch from 'algoliasearch/lite';
// eslint-disable-next-line no-unused-vars
import { InstantSearch, Configure, Hits, SearchBox, useSearchBox, ClearRefinements, CurrentRefinements, useConnector, useHits, useInstantSearch} from 'react-instantsearch';
// import { connectStateResults } from "react-instantsearch/connectors";
import { history } from 'instantsearch.js/es/lib/routers';
import { Stats } from './components/stats';
import { ResultCount } from './components/result-count';
import { Pagination, PaginationButtons } from './components/pagination';

const searchClient = algoliasearch(process.env.MIX_ALGOLIA_APP_ID, process.env.MIX_ALGOLIA_SEARCH_API_KEY);

let timerId = undefined;

let timeout = 200;

const queryHook = (query, search) => {
    if (timerId) {
        clearTimeout(timerId);
    }

    timerId = setTimeout(() => search(query), timeout);
};

const Hit = ({ hit, baseUrl }) => {
    hit = hit.hit;

    if (window.filtersInit) {
        document.getElementById('listing').scrollIntoView({behavior: 'instant'});
    } else {
        setTimeout(function() {
            window.filtersInit = true;
        }, 1000)
    }

    return (
        <div className="mb-6">
            <div className="">
                {hit.type && <span className="block mb-1 text-xs text-green font-bold">{hit.type}</span>}

                <a href={baseUrl + hit.url}
                    className="leading-snug font-bold decoration-transparent underline transition ease-linear duration-300 md:text-lg md:leading-snug hover:decoration-current">
                    {hit.seoTitle}
                </a>
            </div>

            <div className="mt-1 text-sm leading-5 line-clamp-4 md:line-clamp-2 md:mr-10">
                <span className="opacity-50">
                    { hit.data.postDate }
                </span>
                {hit.description &&
                    <span className="ml-1 md:text-base">
                    - {hit.description}
                    </span>
                }
            </div>

            <div className="mt-2.5 line-clamp-1">
                <a href={baseUrl + hit.url} className="text-grey-500 text-sm leading-snug transition ease-linear duration-300 md:text-[0.9375rem] hover:text-green">
                    {baseUrl + hit.url}
                </a>
            </div>

            <div className="h-0.5 bg-grey-100 mt-6"></div>
        </div>
    );
};

const NoResultsBoundary = ({ children, fallback }) => {
    const { results } = useInstantSearch();

    // The `__isArtificial` flag makes sure not to display the No Results message
    // when no hits have been returned.
    if (!results.__isArtificial && results.nbHits === 0) {
        return (
            <>
                {fallback}
                <div hidden>{children}</div>
            </>
        );
    }

    return children;
}

const NoResults = ({noresultsHeading, translationsFilterClear}) => {
    const { indexUiState } = useInstantSearch();

    return (
        <div
            className="border-2 border-grey-200 rounded p-6 sm:p-8 text-center max-w-screen-md mx-auto w-full">
            <p className="font-bold text-green text-xl">{noresultsHeading} <q>{indexUiState.query}</q></p>

            <ClearRefinements
                excludedAttributes={[]}
                classNames={{
                    root: 'w-full group mt-3',
                    button: 'cursor-pointer underline text-grey-400 text-sm leading-none transition duration-300 ease-linear hover:text-green',
                    disabledButton: 'hidden',
                }}
                translations={{
                    resetButtonText: translationsFilterClear,
                }}
            />
        </div>
    );
}

const Search = (props) => {

    const indexName = process.env.MIX_ALGOLIA_INDEX_SEARCH;

    const routing = {
        router: history({
            createURL({qsModule, routeState, location}) {
                const queryParameters = {};

                if (routeState.query) {
                    queryParameters.query = encodeURIComponent(routeState.query);
                }
                if (routeState.page !== 1) {
                    queryParameters.page = routeState.page;
                }

                const queryString = qsModule.stringify(queryParameters, {
                    addQueryPrefix: true,
                    arrayFormat: 'comma',
                    encode: false,
                });

                return `${location.pathname}${queryString}`;
            },

            parseURL({ qsModule, location }) {
                const { query = '', page} = qsModule.parse(
                    location.search.slice(1),
                );

                return {
                    query: decodeURIComponent(query),
                    page,
                };
            },
        }),
        stateMapping: {
            stateToRoute(uiState) {
                const indexUiState = uiState[indexName];

                return {
                    query: indexUiState.query,
                    page: indexUiState.page,
                };
            },
            routeToState(routeState) {

                return {
                    [indexName]: {
                        query: routeState.query,
                        page: routeState.page,
                    },
                };
            },
        },
    };

    return (
        <InstantSearch searchClient={searchClient} indexName={indexName} routing={routing}>
            <Configure
                hitsPerPage={props.perpage}
                filters={'language:' + props.language + ' AND enabled:1'}
            />

            <SearchBox
                queryHook={queryHook}
                classNames={{
                    root: 'js-form-label js-ready',
                    form: 'relative',
                    input: 'styled-input has-icon',
                    submit: 'absolute top-0 right-0 p-3 text-green',
                }}
                submitIconComponent={({classNames}) => (
                    <div className={classNames.submitIcon}>
                        <svg width="25" height="24" viewBox="0 0 25 24" xmlns="http://www.w3.org/2000/svg">
                            <g stroke="currentColor" strokeWidth="2.5" fill="none" fillRule="evenodd">
                                <path
                                    d="M18.094 4.024a9.473 9.473 0 010 13.394 9.464 9.464 0 01-13.389 0 9.473 9.473 0 010-13.394 9.464 9.464 0 0113.389 0h0zM17.994 17.326l5.722 5.724"></path>
                            </g>
                        </svg>
                        <span className="sr-only">Search</span>
                    </div>
                )}
                resetIconComponent={() => (
                    <></>
                )}
                placeholder={props.translationsSearch}
            />

            <div
                className="flex flex-col flex-wrap justify-between gap-6 mt-4 mb-8 md:mb-14 lg:flex-row lg:flex-nowrap">

                <div className="relative flex-1 flex flex-col gap-6 w-full">

                    <ResultCount
                        text={props.translationsResultCount}
                    />

                    <NoResultsBoundary fallback={<NoResults noresultsHeading={props.noresultsHeading} translationsFilterClear={props.translationsFilterClear} />}>
                        <Hits hitComponent={(hit) => <Hit hit={hit} baseUrl={props.baseurl}/>} />
                    </NoResultsBoundary>

                    <div className="mt-6">
                        <div className="flex-1 flex flex-wrap justify-between sm:hidden">

                            <div className="w-full mb-8 text-center">
                                <Stats
                                    text={props.translationsStats}
                                />
                            </div>

                            <PaginationButtons
                                next={props.translationsPaginationNext}
                                previous={props.translationsPaginationPrevious}
                            />
                        </div>

                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <Stats
                                    text={props.translationsStats}
                                />
                            </div>
                            <div className="pl-8">
                                <Pagination
                                    showFirst={false}
                                    showLast={false}
                                    padding={2}
                                    classNames={{
                                        root: 'pl-8',
                                        list: 'relative z-0 inline-flex text-lg',
                                        link: 'px-4 py-2',
                                        item: 'relative inline-flex items-center px-4 py-2 bg-white leading-5 font-normal text-grey transition ease-linear duration-300 hover:text-green',
                                        selectedItem: '!text-white !bg-grey !rounded-sm',
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>

        </InstantSearch>
    );

}

export default Search;
